import {
  useDeleteVisitsVisitIdNotesNoteId,
  useDeleteVisitsVisitIdPatientInstructionsPatientInstructionId,
} from '@/api/generated/visit/visit'

/**
 * The two kinds of medical record an admin can delete from a visit, one record at a time
 * (QMD-4870). Each maps to its own backend endpoint but shares the same request contract.
 */
export type MedicalRecordKind = 'note' | 'patient-instruction'

type DeleteVars = { visitId: string; medicalRecordId: string; reason: string }
type MutateOpts = { onSuccess?: () => void; onError?: (error: unknown) => void }

/**
 * Wraps the generated delete-note / delete-patient-instruction mutation hooks (QMD-4870)
 * behind a single `kind`-selected interface, mirroring `useDeleteLicense`. Unlike that hook
 * there is no optimistic cache edit — the visit's notes/instructions live in the legacy Redux
 * `pastVisitReducer`, not React Query — so the caller refreshes by re-dispatching
 * `getVisitInfo` (and the remarks thunk) in its `onSuccess`. The record is addressed by the
 * `Id` the visit details return for it; the body is the generated
 * `DeleteVisitMedicalRecordsAdminApiInContract` (`{ Reason }`), shared by both endpoints.
 */
export function useDeleteVisitMedicalRecord(kind: MedicalRecordKind) {
  const deleteNote = useDeleteVisitsVisitIdNotesNoteId()
  const deleteInstruction = useDeleteVisitsVisitIdPatientInstructionsPatientInstructionId()

  function mutate({ visitId, medicalRecordId, reason }: DeleteVars, opts?: MutateOpts) {
    const callbacks = {
      onSuccess: () => opts?.onSuccess?.(),
      onError: (error: unknown) => opts?.onError?.(error),
    }
    if (kind === 'note') {
      deleteNote.mutate({ visitId, noteId: medicalRecordId, data: { Reason: reason } }, callbacks)
    } else {
      deleteInstruction.mutate({ visitId, patientInstructionId: medicalRecordId, data: { Reason: reason } }, callbacks)
    }
  }

  const isPending = kind === 'note' ? deleteNote.isPending : deleteInstruction.isPending

  return { mutate, isPending }
}
